import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuService } from '../../services/menu.service';
import { AuthService } from '@services/core/auth.service';
import { CdkDragDrop, moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { FONT_AWESOME_ICONS } from '../../../../../core/constants/icons.constant';
import { routes as APP_ROUTES } from '../../../../../app.routes';
import { CatalogService } from '@services/data/catalog.service';
import { PermissionService } from '@services/core/permission.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-menus-management',
  standalone: true,
  imports: [CommonModule, DragDropModule, FormsModule, NgSelectModule, PageHeaderComponent, MobileFooterComponent],
  templateUrl: './menus-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './menus-management.component.css'
})
export class MenusManagementComponent implements OnInit {
  private menuService = inject(MenuService);
  private authService = inject(AuthService);
  private catalogService = inject(CatalogService);
  readonly perm = inject(PermissionService);
  public appSettings = inject(AppSettings);

  icons = FONT_AWESOME_ICONS;
  routes = APP_ROUTES
    .filter((r) => r.path && r.path !== '**')
    .map((r) => ({ label: r.data?.['title'] || r.path, value: r.path }));

  menus = signal<any[]>([]);
  editingItem = signal<any>(null);
  isDirty = signal(false);
  originalMenus: any[] = [];

  ngOnInit() { this.loadMenus(); }

  loadMenus() {
    this.menuService.get().subscribe((data) => {
      const structured = this.buildFlatTree(data);
      this.menus.set(structured);
      this.originalMenus = JSON.parse(JSON.stringify(structured));
      this.isDirty.set(false);
    });
  }

  menuKey(item: any): string {
    return String(item?.uuid ?? item?.id ?? '');
  }

  menuParentKey(item: any): string | null {
    const raw = item?.parentUuid ?? item?.parentId;
    return raw != null && raw !== '' ? String(raw) : null;
  }

  drop(event: CdkDragDrop<any[]>) {
    const list = [...this.menus()];
    moveItemInArray(list, event.previousIndex, event.currentIndex);
    list.forEach((item, index) => item.order = index + 1);
    this.menus.set(list);
    this.isDirty.set(true);
  }

  menusFilter(item: any) {
    const k = this.menuKey(item);
    return this.menus().filter((m) => !this.menuParentKey(m) && this.menuKey(m) !== k);
  }

  buildInputOptions(options: any[]) {
    return Object.fromEntries(options.map((p) => [this.menuKey(p), p.name]));
  }

  indent(item: any) {
    const options = this.menusFilter(item);
    if (!options.length) return this.showToast('No hay menús raíz disponibles', 'error');

    const inputOptions = this.buildInputOptions(options);
    Swal.fire({
      title: 'Seleccionar Padre',
      text: `¿Cuál será el menú superior de "${item.name}"?`,
      input: 'select', inputOptions,
      inputPlaceholder: 'Selecciona un padre...',
      showCancelButton: true,
      confirmButtonText: 'Asignar', cancelButtonText: 'Cancelar',
      inputValidator: (v) => !v ? 'Debes seleccionar un padre' : null
    }).then((result) => {
      if (!result.isConfirmed) return;
      item.parentUuid = result.value;
      item.parentId = null;
      this.isDirty.set(true);
      this.menus.set(this.buildFlatTree([...this.menus()]));
      this.showToast('Asignado como hijo correctamente');
    });
  }

  outdent(item: any) {
    if (!this.menuParentKey(item)) return;
    item.parentUuid = null;
    item.parentId = null;
    this.isDirty.set(true);
    this.menus.set(this.buildFlatTree([...this.menus()]));
    this.showToast(`"${item.name}" ahora es raíz`);
  }

  createNewMenu() {
    const newMenu = { name: 'Nuevo Menú', icon: 'fas fa-link', path: '/', order: this.menus().length + 1, isActive: true };
    this.menuService.create(newMenu).subscribe(() => this._refreshAll());
  }

  editItem(item: any) { this.editingItem.set({ ...item }); }
  cancelEdit() { this.editingItem.set(null); }

  updateItem() {
    const edited = this.editingItem();
    if (!edited) return;
    this.menuService.update(this.menuKey(edited), edited).subscribe(() => {
      this.editingItem.set(null);
      this._refreshAll();
    });
  }

  deleteItem(item: any) {
    const key = this.menuKey(item);
    Swal.fire({
      title: '¿Estás seguro?', text: 'Esta acción no se puede deshacer',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.menuService.delete(key).subscribe(() => this._refreshAll());
    });
  }

  saveChanges() {
    const dataToSave = this.menus().map((m) => ({
      uuid: m.uuid,
      id: m.id,
      parentUuid: m.parentUuid,
      parentId: m.parentId,
      order: m.order
    }));
    this.menuService.bulkUpdate(dataToSave).subscribe({
      next: () => { this.showToast('Cambios guardados correctamente'); this._refreshAll(); },
      error: () => this.showToast('Error al guardar', 'error')
    });
  }

  private _refreshAll() {
    this.loadMenus();
    this.catalogService.loaded.set(false);
    this.catalogService.loadCatalogs();
    this.authService.refreshBudgetVersion().subscribe();
  }

  private showToast(title: string, icon: 'success' | 'error' = 'success') {
    Swal.fire({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true, icon, title });
  }

  private buildFlatTree(list: any[]): any[] {
    const map = Object.fromEntries(list.map((i) => [this.menuKey(i), { ...i, children: [] }]));
    const roots: any[] = [];
    list.forEach((item) => {
      const pKey = this.menuParentKey(item);
      pKey && map[pKey] ? map[pKey].children.push(map[this.menuKey(item)]) : roots.push(map[this.menuKey(item)]);
    });
    return this._flattenSorted(roots, []);
  }

  private _flattenSorted(nodes: any[], result: any[]): any[] {
    nodes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
         .forEach((node) => { result.push(node); this._flattenSorted(node.children, result); });
    return result;
  }
}
